<template>
  <default-layout>

    <breadcrumbs :routes="breadcrumbs"></breadcrumbs>

    <h2 v-if="emailTemplateId">
      Edit Email Template
    </h2>
    <h2 v-else>
      Add Email Template
    </h2>

    <form-form @submit="handleSubmit" :submitError="submitError" :validator="$v" v-if="isInitialized">
      <form-input type="text"
                  label="Template Name (for internal reference)"
                  v-model="localTemplate.name"
                  :validator="$v.localTemplate.name">
      </form-input>

      <form-input ref="subject" label="Subject Line" v-model="localTemplate.subject"></form-input>
      <text-complete :element="$refs.subject ? $refs.subject.element : null"
                     :strategies="['mergeTags']"
                     :merge-tags="templateMergeTags"
                     v-if="templateMergeTags">
      </text-complete>

      <div class="margin-bottom">
        <quill-editor :merge-tags="templateMergeTags"
                      v-model="localTemplate.data"
                      @input="hasChanges = true"
                      ref="quill"></quill-editor>
      </div>

      <div class="margin-bottom">
        <email-attachment
          show-button
          :attachments="localTemplate.attachments"
          @data="localTemplate.attachments = $event" />
      </div>

      <form-select label="Default Email Account to Send From"
                   empty="No default"
                   :options="emailAccountOptions"
                   v-model="localTemplate.email_account_id"
                   v-if="emailAccountOptions.length > 0"></form-select>
      <form-tags-input v-model="localTemplate.cc"
                       validate-email
                       label="Default Cc"
                       placeholder="Add Email"></form-tags-input>
      <form-tags-input v-model="localTemplate.bcc"
                       validate-email
                       label="Default Bcc"
                       placeholder="Add Email"></form-tags-input>

      <template v-if="emailTemplate">
        <div class="grid-x">
          <div class="cell auto">
            <button class="button hollow" @click="handleSubmit()" :disabled="isSaving">Save</button>&nbsp;
            <button class="button" @click="handleSubmit(true)" :disabled="isSaving">Save &amp; Exit</button>
          </div>
          <div class="cell shrink">
            <a class="button alert clear float-right" @click="handleDelete">Delete Template</a>
          </div>
        </div>
      </template>
      <template v-else>
        <button class="button" @click="handleSubmit(true)" :disabled="isSaving">Save Template</button>
      </template>
    </form-form>

  </default-layout>
</template>

<script>
import QuillEditor from '../../components/TemplateEditors/QuillEditor'
import { formsMixin } from '../../mixins/forms/formsMixin'
import {
  emailTemplateQuery,
  templateMergeTagsQuery,
  addEmailTemplateMutation,
  deleteEmailTemplateMutation,
  updateEmailTemplateMutation
} from '../../graphql/emailTemplates'
import { emailAccountsQuery } from '../../graphql/email'
import { required } from 'vuelidate/lib/validators'
import DefaultLayout from '../../layouts/DefaultLayout'
import Breadcrumbs from '../../components/Common/Breadcrumbs'
import TextComplete from '../../components/Common/TextComplete'
import EmailAttachment from '../../components/Emails/EmailAttachment'

export default {
  mixins: [
    formsMixin
  ],
  components: {
    EmailAttachment,
    Breadcrumbs,
    DefaultLayout,
    QuillEditor,
    TextComplete
  },
  data () {
    return {
      submitError: null,
      hasChanges: false,
      isSaving: false,
      localTemplate: null, // gets set after initializing the email template
      editorObject: {},
      breadcrumbs: [
        { route: { name: 'templates' }, text: 'Templates' },
        { route: { name: 'emailTemplates' }, text: 'Email Templates' },
        { text: 'Template' }
      ]
    }
  },
  apollo: {
    templateMergeTags: templateMergeTagsQuery,
    emailAccounts: emailAccountsQuery,
    emailTemplate: {
      query: emailTemplateQuery,
      variables () {
        return {
          id: this.emailTemplateId
        }
      },
      update (response) {
        const template = response.emailTemplate
        // create a mutable copy of the record and set it to the local scope
        const localTemplate = Object.assign({}, template)
        localTemplate.data = localTemplate.data ? JSON.parse(localTemplate.data) : localTemplate.data
        this.localTemplate = localTemplate
        return template
      },
      skip () {
        if (this.emailTemplateId) {
          // run the query
          return false
        }
        // otherwise, stop the query and use an empty local template
        this.localTemplate = {}
        return true
      }
    }
  },
  computed: {
    emailTemplateId () {
      return this.$route.params.id
    },
    isInitialized () {
      // wait for everything we need to load
      return !!this.templateMergeTags && !!this.localTemplate
    },
    emailAccountOptions () {
      if (!this.emailAccounts) {
        return []
      }
      return this.emailAccounts.map(emailAccount => {
        return {
          value: emailAccount.id,
          text: emailAccount.email
        }
      })
    }
  },
  validations: {
    localTemplate: {
      name: {
        required
      }
    }
  },
  methods: {
    async handleSubmit (redirectAfterSave) {
      this.isSaving = true
      // wait one event cycle so that any CC/BCC tags have time to be added to the data before the
      // form submits. This can be a problem if someone has typed in an email address but it hasn't converted
      // to a tag yet, and then they click directly onto the submit button.
      await new Promise((resolve) => {
        setTimeout(resolve)
      })
      // format the attachment objects
      const attachments = (this.localTemplate.attachments || []).map(attachment => {
        return {
          upload_id: attachment.upload_id,
          filename: attachment.filename,
          file_type: attachment.file_type
        }
      })
      // get the html
      const html = this.$refs.quill.getHtml()
      const input = {
        name: this.localTemplate.name,
        subject: this.localTemplate.subject,
        data: JSON.stringify(this.localTemplate.data),
        html: html,
        attachments: attachments,
        cc: this.localTemplate.cc,
        bcc: this.localTemplate.bcc,
        email_account_id: this.localTemplate.email_account_id
      }
      // update the email template
      try {
        if (this.emailTemplate) {
          await this.updateTemplate(input)
        } else {
          await this.addTemplate(input)
        }
        this.isSaving = false
        // clear the changes flag
        this.hasChanges = false
        // redirect?
        if (redirectAfterSave) {
          this.$notifications.feedbackNotification('Template saved', 'success')
          return this.$router.push({ name: 'emailTemplates' })
        }
      } catch (e) {
        this.isSaving = false
        this.submitError = e
      }
    },
    async handleDelete () {
      if (confirm('Are you sure that you want to delete this email template?')) {
        try {
          const id = this.emailTemplate.id
          await this.$apollo.mutate({
            mutation: deleteEmailTemplateMutation,
            variables: {
              id: id
            }
          })
          // redirect
          this.$notifications.feedbackNotification('Template deleted', 'success')
          return this.$router.push({ name: 'emailTemplates' })
        } catch (e) {
          this.$notifications.feedbackNotification('Failed to delete the template. ' + e.message, 'error')
        }
      }
    },
    addTemplate (input) {
      return this.$apollo.mutate({
        mutation: addEmailTemplateMutation,
        variables: {
          input: input
        }
      })
    },
    updateTemplate (input) {
      return this.$apollo.mutate({
        mutation: updateEmailTemplateMutation,
        variables: {
          id: this.emailTemplate.id,
          input: input
        }
      })
    }
  }
  // beforeRouteLeave (to, from, next) {
  //   if (this.hasChanges) {
  //     if (window.confirm('Do you want to save your changes before you leave?')) {
  //       // save changes before leaving page
  //       this.handleSubmit()
  //         .then(() => next())
  //         .catch(() => next(false))
  //     } else {
  //       next(false)
  //     }
  //   } else {
  //     next()
  //   }
  // }
}
</script>
